import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { BACKEND_ORIGIN } from "../lib/api";

const WSContext = createContext(null);

// Códigos WebSocket que indicam falha de autenticação — não tentar reconectar
const WS_AUTH_CLOSE_CODES = new Set([1008]);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef({}); // event -> Set<handler>
  // Evita múltiplas tentativas paralelas de reconexão
  const retryRef = useRef(null);
  const mountedRef = useRef(true);

  const on = useCallback((event, handler) => {
    listenersRef.current[event] = listenersRef.current[event] || new Set();
    listenersRef.current[event].add(handler);
    return () => listenersRef.current[event]?.delete(handler);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!user || typeof user !== "object") return;

    const token = localStorage.getItem("cliver_token");
    const wsBase = BACKEND_ORIGIN.replace(/^http/, "ws");
    // Envia token via query string (o backend também aceita cookie como fallback)
    const wsUrl = token
      ? `${wsBase}/api/ws?token=${encodeURIComponent(token)}`
      : `${wsBase}/api/ws`;

    let retryDelay = 3000;

    const connect = () => {
      if (!mountedRef.current) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        setConnected(true);
        retryDelay = 3000; // reset backoff on success
      };

      ws.onclose = (ev) => {
        if (!mountedRef.current) return;
        setConnected(false);

        // Não reconectar em caso de falha de autenticação (code 1008)
        // O backend fecha com 1008 quando o token é inválido/ausente.
        // Uma nova reconexão apenas geraria mais erros — aguardar reload/relogin.
        if (WS_AUTH_CLOSE_CODES.has(ev.code)) {
          return;
        }

        // Backoff leve para desconexões normais (rede, restart do servidor)
        retryRef.current = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 1.5, 30000); // max 30s
      };

      ws.onerror = () => {
        // Erros são tratados via onclose — não logar para não poluir o console
      };

      ws.onmessage = (ev) => {
        try {
          const { event, payload } = JSON.parse(ev.data);
          const set = listenersRef.current[event];
          if (set) set.forEach((h) => h(payload));
        } catch {
          // mensagem malformada — ignorar silenciosamente
        }
      };
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [user]);

  return (
    <WSContext.Provider value={{ connected, on }}>
      {children}
    </WSContext.Provider>
  );
}

export const useWS = () => useContext(WSContext) || { connected: false, on: () => () => {} };
